const SAFE_PLACES = {
  frontdoor: 'Leave by front door', backdoor: 'Leave by back door',
  mailroom: 'Leave in mailroom', shed: 'Leave in shed', bygarage: 'Leave by garage',
  greenhouse: 'Leave in greenhouse', garden: 'Leave in garden',
  behindbin: 'Leave behind wheelie bin', concierge: 'Leave with concierge',
  neighbour: 'Leave with neighbour',
};

export function mergeSafePlace(note, previous, next) {
  let text = note.trim();
  const old = SAFE_PLACES[previous] || '';
  const addition = SAFE_PLACES[next] || '';
  if (old && text === old) text = '';
  else if (old && text.startsWith(old + '. ')) text = text.slice(old.length + 2);
  if (!addition || text === addition || text.startsWith(addition + '. ')) return text;
  return text ? `${addition}. ${text}` : addition;
}

function methodOf(value) {
  if (/pickup|pick.?up|collection/i.test(value || '')) return 'pickup';
  if (/shipping|delivery/i.test(value || '')) return 'shipping';
  return null;
}

export function mountOrderNotes(document) {
  const states = new Set();
  const bound = new WeakSet();
  let draft = null;
  let saved = '';
  let queue = Promise.resolve();
  let pendingSaves = 0;
  let replaying = false;
  let counter = 0;
  const view = document.defaultView;
  const payloadFor = state => {
    const delivery = state.method === 'pickup' ? '' : state.delivery.value.replace(/[\r\n]+/g, ' ').trim();
    return { note: delivery, attributes: {
      'Delivery instructions': delivery, 'Gift message': state.gift.value,
      'safe-place': state.method === 'pickup' ? '' : state.safe.value,
      'Note fields version': '1',
    } };
  };
  function status(state, message, error = false) {
    state.status.textContent = message;
    state.status.dataset.error = String(error);
  }
  function reflect(state) {
    const collection = state.method === 'pickup';
    state.root.querySelector('[data-shop-safe-place]').hidden = collection;
    state.root.querySelector('[data-shop-delivery-field]').hidden = collection;
    for (const advice of state.root.querySelectorAll('[data-shop-delivery-advice]')) advice.hidden = collection;
    for (const advice of state.root.querySelectorAll('[data-shop-collection-advice]')) advice.hidden = !collection;
    state.safe.disabled = state.delivery.disabled = collection;
    state.mirror.value = collection ? '' : state.delivery.value;
    state.count.textContent = `${state.delivery.value.length}/${state.limit} characters`;
  }
  function capture(state) {
    draft = { delivery: state.delivery.value, gift: state.gift.value, safe: state.safe.value,
      giftScope: state.giftScope?.value || 'first-order-only' };
    for (const other of states) {
      if (!other.root.isConnected) { states.delete(other); continue; }
      if (other !== state) {
        other.delivery.value = draft.delivery; other.gift.value = draft.gift; other.safe.value = draft.safe;
      }
      reflect(other);
    }
  }
  function validate(state) {
    if (state.method !== 'pickup' && state.delivery.value.length > state.limit) {
      throw Error(`Please shorten delivery instructions to ${state.limit} characters. Put any gift message in its separate field.`);
    }
    if (state.gift.value.length > 1000) throw Error('Please shorten the gift message to 1,000 characters.');
  }
  function save(state) {
    pendingSaves++;
    // Serialize writes across old/new Ajax cart renders. Read the latest draft
    // when each queued write begins, never replay an older captured payload.
    queue = queue.catch(() => {}).then(async () => {
      validate(state);
      const payload = payloadFor(state);
      const signature = JSON.stringify(payload);
      if (signature === saved) { status(state, 'Notes saved.'); return; }
      status(state, 'Saving notes…');
      const controller = new view.AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      let response;
      try {
        response = await view.fetch(`${view.Shopify?.routes?.root || '/'}cart/update.js`, {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload), signal: controller.signal,
        });
      } finally { clearTimeout(timeout); }
      if (!response.ok) throw Error('Your notes could not be saved. Please try checkout again.');
      const cart = await response.json();
      if ((cart.note || '') !== payload.note ||
          Object.entries(payload.attributes).some(([key, value]) => (cart.attributes?.[key] || '') !== value)) {
        throw Error('Your notes could not be confirmed. Please try checkout again.');
      }
      saved = signature;
      status(state, 'Notes saved.');
    }).catch(error => {
      status(state, error.name === 'AbortError' ? 'Saving notes timed out. Please try checkout again.' : error.message, true);
      throw error;
    }).finally(() => { pendingSaves--; });
    return queue;
  }
  function schedule(state) {
    capture(state);
    clearTimeout(state.timer);
    state.timer = setTimeout(() => { save(state).catch(() => {}); }, 350);
  }
  function suppressLegacy(state) {
    for (const input of state.form.querySelectorAll('textarea[name="note"]')) {
      if (state.root.contains(input)) continue;
      input.disabled = true; input.hidden = true; input.dataset.shopLegacyNote = 'true';
      for (const label of state.form.querySelectorAll('label[for]')) if (label.htmlFor === input.id) {
        label.hidden = true; label.dataset.shopLegacyNote = 'true';
      }
    }
    // Zapiet's original English note toggle is now redundant. Hide only the
    // inspected text control, never an ancestor/configuration container.
    for (const control of state.form.querySelectorAll('a,button,label,summary,span,p,[role="button"],div:not(:has(> *))')) {
      if (!state.root.contains(control) &&
          /^[+\-−–]?\s*Leave a note or gift message about your order\.?$/i.test(control.textContent.trim())) {
        control.hidden = true; control.dataset.shopLegacyNote = 'true';
      }
    }
  }
  function syncMethod(state) {
    const active = state.form.querySelector('.checkoutMethod.active,.checkoutMethod[aria-pressed="true"]');
    const hidden = state.form.querySelector('[name="attributes[Checkout-Method]"]');
    const method = methodOf(active?.dataset.method || active?.dataset.checkoutMethod || active?.textContent)
      || methodOf(hidden?.value) || methodOf(state.root.dataset.checkoutMethod);
    if (method && method !== state.method) { state.method = method; reflect(state); schedule(state); }
  }
  function bind(root) {
    if (bound.has(root)) return;
    const form = root.closest('form');
    if (!form) return;
    const state = { root, form, method: methodOf(root.dataset.checkoutMethod) || 'shipping',
      limit: Number(root.dataset.deliveryNoteLimit), delivery: root.querySelector('[data-shop-delivery-note]'),
      gift: root.querySelector('[data-shop-gift-note]'), giftScope: root.querySelector('[data-shop-gift-scope]'),
      safe: root.querySelector('[data-shop-safe-select]'),
      count: root.querySelector('[data-shop-delivery-counter]'), status: root.querySelector('[data-shop-note-status]') };
    if (!Number.isInteger(state.limit) || state.limit < 1) throw Error('Delivery note limit is missing');
    bound.add(root); states.add(state);
    state.delivery.maxLength = state.limit;
    state.mirror = document.createElement('input');
    state.mirror.type = 'hidden'; state.mirror.name = 'note'; root.append(state.mirror);
    for (const input of [state.delivery, state.gift, state.safe]) {
      const oldId = input.id;
      input.id = `${oldId}-${++counter}`;
      for (const label of root.querySelectorAll('label')) if (label.htmlFor === oldId) label.htmlFor = input.id;
    }
    if (draft) {
      state.delivery.value = draft.delivery; state.gift.value = draft.gift; state.safe.value = draft.safe;
      if (state.giftScope) state.giftScope.value = draft.giftScope || 'first-order-only';
    } else if (root.dataset.deliveryNotePresent !== 'true') {
      const seeded = mergeSafePlace(state.delivery.value, '', state.safe.value);
      if (seeded.length <= state.limit) state.delivery.value = seeded;
    }
    let previous = state.safe.value;
    state.safe.addEventListener('change', () => {
      const merged = mergeSafePlace(state.delivery.value, previous, state.safe.value);
      if (merged.length > state.limit) {
        state.safe.value = previous;
        status(state, `That choice would exceed ${state.limit} characters. Shorten the delivery instructions first.`, true);
        state.delivery.focus(); return;
      }
      state.delivery.value = merged; previous = state.safe.value;
      if (previous === 'noneofabove' || previous === 'neighbour') state.delivery.focus();
      schedule(state);
    });
    state.delivery.addEventListener('input', () => {
      if (!state.delivery.value) state.safe.value = 'anysecurelocation';
      previous = state.safe.value;
      schedule(state);
    });
    state.gift.addEventListener('input', () => schedule(state));
    state.giftScope?.addEventListener('change', () => schedule(state));
    reflect(state); suppressLegacy(state); syncMethod(state); schedule(state);
    const checkout = event => {
      if (replaying) return;
      const control = event.target.closest('button,input,a');
      if (!control || !(control.name === 'checkout' || control.id === 'Checkout' ||
          /^\/checkouts?(\/|$)/.test(control.getAttribute('href') || ''))) return;
      clearTimeout(state.timer);
      capture(state);
      if (pendingSaves === 0 && JSON.stringify(payloadFor(state)) === saved) return;
      event.preventDefault(); event.stopImmediatePropagation();
      save(state).then(() => {
        if (JSON.stringify(payloadFor(state)) !== saved) return;
        replaying = true;
        try { control.click(); } finally { replaying = false; }
      }).catch(() => {});
    };
    form.addEventListener('click', checkout, true);
    form.addEventListener('submit', event => {
      if (replaying || !event.submitter || event.submitter.name !== 'checkout') return;
      clearTimeout(state.timer); capture(state);
      if (pendingSaves === 0 && JSON.stringify(payloadFor(state)) === saved) return;
      event.preventDefault(); event.stopImmediatePropagation();
      save(state).then(() => {
        if (JSON.stringify(payloadFor(state)) !== saved) return;
        replaying = true;
        try { form.requestSubmit(event.submitter); } finally { replaying = false; }
      }).catch(() => {});
    }, true);
  }
  const scan = () => {
    for (const root of document.querySelectorAll('[data-shop-order-notes]')) bind(root);
    for (const state of states) if (state.root.isConnected) { suppressLegacy(state); syncMethod(state); }
  };
  view.goldsteinFlushOrderNotes = async () => {
    // A quantity update remounts the basket. Save the customer's notes first.
    scan();
    for (const state of states) {
      if (!state.root.isConnected) continue;
      clearTimeout(state.timer);
      capture(state);
      await save(state);
    }
  };
  document.addEventListener('click', event => {
    const methodControl = event.target.closest('.checkoutMethod');
    if (!methodControl) return;
    const method = methodOf(methodControl.dataset.method || methodControl.dataset.checkoutMethod || methodControl.textContent);
    for (const state of states) if (state.form.contains(methodControl) && method) {
      state.method = method; reflect(state); schedule(state);
    }
  }, true);
  document.addEventListener('change', event => {
    if (event.target.name === 'attributes[Checkout-Method]') scan();
  });
  const observer = new view.MutationObserver(records => {
    if (records.every(record => record.target.closest?.('[data-shop-order-notes]'))) return;
    scan();
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true,
    attributeFilter: ['class', 'aria-pressed', 'value'] });
  scan();
}

if (typeof document !== 'undefined') mountOrderNotes(document);

/* goldstein-checkout-note-acknowledgement */
